export type ChatSegment =
  | { type: 'text'; text: string }
  | { type: 'mention'; handle: string }
  | { type: 'link'; href: string; text: string };

const MENTION = /@([a-z0-9_]{3,24})\b/gi;
const LINK = /\bhttps?:\/\/[^\s<>"']{1,300}/gi;

/** Unique, lower-cased @handles (max 5) — reply-free mentions. */
export function extractMentions(body: string): string[] {
  const out = new Set<string>();
  for (const m of body.matchAll(MENTION)) {
    out.add(m[1]!.toLowerCase());
    if (out.size >= 5) break;
  }
  return [...out];
}

/** Collapse whitespace and strip control chars; the UI always renders the result as text. */
export function normalizeChatBody(body: string): string {
  return body
    .replace(/[\u0000-\u0008\u000b-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Split a message into text / mention / link segments for safe rendering. Links are only
 * http(s); no previews are ever fetched.
 */
export function tokenizeChat(body: string): ChatSegment[] {
  type Hit = { start: number; end: number; seg: ChatSegment };
  const hits: Hit[] = [];
  for (const m of body.matchAll(LINK)) {
    let href = m[0];
    // Trim trailing punctuation that is almost never part of the URL.
    while (/[.,;:!?)\]]$/.test(href)) href = href.slice(0, -1);
    try {
      const u = new URL(href);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') continue;
    } catch {
      continue;
    }
    hits.push({
      start: m.index!,
      end: m.index! + href.length,
      seg: { type: 'link', href, text: href },
    });
  }
  for (const m of body.matchAll(MENTION)) {
    const start = m.index!;
    const end = start + m[0].length;
    if (hits.some((h) => start < h.end && end > h.start)) continue;
    if (start > 0 && /[\w]/.test(body[start - 1]!)) continue; // email-like, not a mention
    hits.push({ start, end, seg: { type: 'mention', handle: m[1]!.toLowerCase() } });
  }
  hits.sort((a, b) => a.start - b.start);
  const out: ChatSegment[] = [];
  let pos = 0;
  for (const h of hits) {
    if (h.start > pos) out.push({ type: 'text', text: body.slice(pos, h.start) });
    out.push(h.seg);
    pos = h.end;
  }
  if (pos < body.length) out.push({ type: 'text', text: body.slice(pos) });
  return out;
}
